import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  email as rule_email,
  min as rule_min,
  max as rule_max,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
  required_if as rule_required_if,
  numeric,
  min_value,
  max_value,
} from 'vee-validate/dist/rules'
import en from 'vee-validate/dist/locale/en.json'
import es from 'vee-validate/dist/locale/es.json'
import pt from 'vee-validate/dist/locale/pt_BR.json';

// eslint-disable-next-line object-curly-newline
import { validatorPositive, validatorUrlValidator, validatorPassword, validatorCreditCard, validatorIP } from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

// Install localizations.
localize({ en, es, pt });

extend('numeric', numeric);
extend('min_value', min_value);
extend('max_value', max_value);

export const required = extend('required', rule_required)

export const email = extend('email', rule_email)

export const min = extend('min', rule_min)

export const max = extend('max', rule_max)

export const confirmed = extend('confirmed', rule_confirmed)

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

export const required_if = extend('required_if', rule_required_if)

export const positive = extend('positive', {
  validate: validatorPositive,
  message: 'Please enter positive number!',
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: 'It is not valid credit card!',
})

export const password = extend('password', {
  validate: validatorPassword,
  message: 'Your {_field_} must contain at least one uppercase, one lowercase, one special character and one digit',
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: 'URL is invalid',
})

export const ip = extend('ip', {
  validate: validatorIP,
  message: 'IP address is invalid',
})

function isIPv4(host) {
  const parts = host.split('.');
  if (parts.length !== 4) return false;
  return parts.every(p => {
    if (!/^\d+$/.test(p)) return false;
    const n = Number(p);
    return n >= 0 && n <= 255;
  });
}

function isHostname(host) {
  if (host === 'localhost') return true;
  const label = '(?!-)[A-Za-z0-9-]{1,63}(?<!-)';
  const re = new RegExp(`^${label}(\\.${label})+$`);
  return re.test(host);
}

export const host_port = extend('host_port', {
  validate(value) {
    if (typeof value !== 'string') return false;

    const idx = value.lastIndexOf(':');
    if (idx <= 0 || idx === value.length - 1) return false;

    const host = value.slice(0, idx).trim();
    const portStr = value.slice(idx + 1).trim();

    if (!/^\d{1,5}$/.test(portStr)) return false;
    const port = Number(portStr);
    if (port < 1 || port > 65535) return false;

    return isIPv4(host) || isHostname(host);
  },
  message:
    'The {_field_} must follow the format [host or ip]:port. Valid examples: x.x.x.x:8080, example.com:9000',
});

export const month_day_format = extend('month_day_format', {
  validate(value) {
    if (!value) return false;
    // Validate MM/DD format (without year)
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/;
    if (!dateRegex.test(value)) return false;

    // Extract month and day
    const [month, day] = value.split('/').map(Number);

    // Validate month
    if (month < 1 || month > 12) return false;

    // Days per month (using leap year for February)
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Validate day
    if (day < 1 || day > daysInMonth[month - 1]) return false;

    return true;
  },
  message: 'The {_field_} must be in MM/DD format',
});

export const required_duration = extend('required_duration', {
  validate(value) {
    if (value == null || value === '') return false;
    let hours = 0;
    let minutes = 0;
    if (typeof value === 'string') {
      const parts = value.split(':');
      hours = Number(parts[0]) || 0;
      minutes = Number(parts[1]) || 0;
    } else if (typeof value === 'object') {
      hours = Number(value.hours) || 0;
      minutes = Number(value.minutes) || 0;
    } else {
      return false;
    }
    return hours > 0 || minutes > 0;
  },
  message: 'The {_field_} is required and must be greater than 0',
});

// localize({
//   en: {
//     messages: en.messages,
//     names: {
//       email: 'Email',
//       password: 'Password',
//     },
//     fields: {
//       password: {
//         min: '{_field_} is too short, you want to get hacked?',
//       },
//     },
//   },
//   es: {
//     messages: es.messages,
//     names: {
//       email: 'E-mail',
//       password: 'Contraseña',
//     },
//     fields: {
//       password: {
//         min: '{_field_} is too short, you want to get hacked?',
//       },
//     },
//   },
// })
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
